import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { PageProps } from "../types/auth";

// Import SVG icons
import PencilIcon from "../assets/icons/pencil.svg";
import TrashIcon from "../assets/icons/trash.svg";

import styles from './styles/NotesPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Note {
  note_id: number;
  org_id: number;
  user_id: number;
  note: string;
  created_at: string;
  author_username: string;
}

interface Member {
  user_id: number;
  username: string;
  display_name?: string;
}

interface Skill {
  skill_id: number;
  name: string;
  description?: string;
}

interface RouteParams {
  [key: string]: string | undefined;
  member_id?: string;
}

interface LocationState {
  selectedCurriculumId?: number;
}

interface Curriculum {
  curriculum_id: number;
  active_level_id: number;
}

interface Level {
  level_id: number;
  name: string;
  skills: Skill[];
}

type SkillsTab = 'level' | 'all';

const NotesPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const { member_id } = useParams<RouteParams>();
  const location = useLocation();
  const { selectedCurriculumId } = (location.state as LocationState) || {};
  const [notes, setNotes] = useState<Note[]>([]);
  const [currentNote, setCurrentNote] = useState<string>("");
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [noteToDelete, setNoteToDelete] = useState<Note | null>(null);
  const [memberInfo, setMemberInfo] = useState<Member | null>(null);
  const [error, setError] = useState<string>("");
  const [activeTab, setActiveTab] = useState<SkillsTab>('level');
  const [allSkills, setAllSkills] = useState<string[]>([]);
  const [levelSkills, setLevelSkills] = useState<string[]>([]);
  const [displayedSkills, setDisplayedSkills] = useState<string[]>([]);
  const [skillsSearch, setSkillsSearch] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const noteTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (noteTextareaRef.current) {
      noteTextareaRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const fetchMemberInfo = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/${member_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch member details.");
          return;
        }

        const data = await response.json();
        setMemberInfo(data);
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    if (member_id) {
      fetchMemberInfo();
    }
  }, [member_id, token, selectedOrgID]);

  useEffect(() => {
    const fetchNotes = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/notes?user_id=${member_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch notes.");
          return;
        }

        const data = await response.json();
        setNotes(Array.isArray(data) ? data : []);
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    fetchNotes();
  }, [member_id, token, selectedOrgID]);

  // Fetch level skills
  useEffect(() => {
    const fetchLevelSkills = async () => {
      if (!member_id || !selectedCurriculumId) return;

      try {
        // First fetch curriculum to get active level ID
        const curriculumResponse = await fetch(
          `${API_BASE_URL}/api/users/${member_id}/curriculums/${selectedCurriculumId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Org-ID": selectedOrgID?.toString() || "",
            },
          }
        );

        if (!curriculumResponse.ok) {
          const errorData = await curriculumResponse.json();
          console.error("Failed to fetch curriculum:", errorData.error);
          return;
        }

        const curriculumData: Curriculum = await curriculumResponse.json();
        
        // Then fetch level details including skills
        const levelResponse = await fetch(
          `${API_BASE_URL}/api/users/${member_id}/levels/${curriculumData.active_level_id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Org-ID": selectedOrgID?.toString() || "",
            },
          }
        );

        if (!levelResponse.ok) {
          const errorData = await levelResponse.json();
          console.error("Failed to fetch level skills:", errorData.error);
          return;
        }

        const levelData: Level = await levelResponse.json();
        const skillNames = levelData.skills.map(skill => skill.name);
        
        setLevelSkills(skillNames);
        setDisplayedSkills(activeTab === 'level' ? skillNames : allSkills);
      } catch (err) {
        console.error("An unexpected error occurred while fetching level skills:", err);
      }
    };

    fetchLevelSkills();
  }, [member_id, selectedCurriculumId, token, selectedOrgID]);

  // Update displayed skills when active tab or skills lists change
  useEffect(() => {
    setDisplayedSkills(activeTab === 'level' ? levelSkills : allSkills);
  }, [activeTab, levelSkills, allSkills]);

  // Fetch all skills in the organization
  useEffect(() => {
    const fetchAllSkills = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/skills`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error("Failed to fetch all skills:", errorData.error);
          return;
        }

        const data = await response.json();
        const skillNames = Array.isArray(data) 
          ? data.map((skill: Skill) => skill.name) 
          : [];
        setAllSkills(skillNames);
        if (activeTab === 'all') {
          setDisplayedSkills(skillNames);
        }
      } catch (err) {
        console.error("An unexpected error occurred while fetching skills:", err);
      }
    };

    if (selectedOrgID) {
      fetchAllSkills();
    }
  }, [token, selectedOrgID, activeTab]);

  const handleTabChange = (tab: SkillsTab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    if (tab === 'level') {
      setSkillsSearch('');
    }
  };

  const handleEdit = async (noteId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/notes/${noteId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to fetch note.");
        return;
      }

      const data: Note = await response.json();
      setCurrentNote(data.note);
      setEditingNoteId(noteId);
      setSelectedDate(new Date(data.created_at).toISOString().split('T')[0]);
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  const handleDelete = async (noteId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/notes/${noteId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to delete note.");
        return;
      }

      setNotes(notes.filter(n => n.note_id !== noteId));
      setNoteToDelete(null);
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentNote.trim()) {
      setError("Note text cannot be empty.");
      return;
    }

    try {
      const url = editingNoteId 
        ? `${API_BASE_URL}/api/notes/${editingNoteId}`
        : `${API_BASE_URL}/api/notes`;
      const method = editingNoteId ? "PUT" : "POST";

      // Get timezone offset in hours:minutes format
      const date = new Date();
      const offsetMinutes = date.getTimezoneOffset();
      const offsetHours = Math.abs(Math.floor(offsetMinutes / 60)).toString().padStart(2, "0");
      const offsetMins = Math.abs(offsetMinutes % 60).toString().padStart(2, "0");
      const offsetSign = offsetMinutes <= 0 ? "+" : "-";

      // Format the date with timezone
      const formattedDate = `${selectedDate}T00:00:00${offsetSign}${offsetHours}:${offsetMins}`;

      const body = editingNoteId
        ? { note: currentNote, created_at: formattedDate }
        : { user_id: parseInt(member_id || "0", 10), note: currentNote, created_at: formattedDate };

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || `Failed to ${editingNoteId ? 'update' : 'create'} note.`);
        return;
      }

      const data: Note = await response.json();
      if (editingNoteId) {
        setNotes(notes.map(n => n.note_id === editingNoteId ? data : n));
      } else {
        setNotes([data, ...notes]);
      }

      setCurrentNote("");
      setEditingNoteId(null);
      setSelectedDate(new Date().toISOString().split('T')[0]);
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  const handleSkillClick = (skillName: string) => {
    setCurrentNote(prev => {
      const skillTag = `Skill worked on: ${skillName}`;
      return prev ? `${prev}\n${skillTag}\n` : `${skillTag}\n`;
    });
  };

  if (error) {
    return <p className={styles.errorMessage}>{error}</p>;
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Notes</h1>
      {memberInfo && (
        <p className={styles.memberInfo}>
          <strong>Member:</strong>{" "}
          <a href={`/members/${member_id}`} className={styles.memberLink}>
          {memberInfo.display_name 
            ? `${memberInfo.display_name} (${memberInfo.username})`
            : memberInfo.username}
          </a>
        </p>
      )}

      <div className={styles.noteEditorContainer}>
        <form onSubmit={handleSubmit} className={styles.noteForm}>
          <div className={styles.datePicker}>
            <label className={styles.dateLabel}>Date:</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className={styles.dateInput}
            />
          </div>
          <div className={styles.noteAndSkillsContainer}>
            <div className={styles.noteTextarea}>
              <textarea
                ref={noteTextareaRef}
                value={currentNote}
                onChange={(e) => setCurrentNote(e.target.value)}
                placeholder="Enter note..."
                rows={10}
                className={styles.textarea}
              />
              <div className={styles.buttonContainer}>
                <button type="submit" className={styles.button}>
                  {editingNoteId ? "Save" : "Create"}
                </button>
                {editingNoteId && (
                  <button 
                    type="button" 
                    onClick={() => {
                      setCurrentNote("");
                      setEditingNoteId(null);
                    }}
                    className={styles.cancelButton}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>

            <div className={styles.skillsList}>
              <div className={styles.skillsTitle}>
                <span 
                  className={activeTab === 'level' ? styles.skillsTabActive : styles.skillsTab}
                  onClick={() => handleTabChange('level')}
                >
                  Level Skills
                </span>
                <span>|</span>
                <span 
                  className={activeTab === 'all' ? styles.skillsTabActive : styles.skillsTab}
                  onClick={() => handleTabChange('all')}
                >
                  All Skills
                </span>
              </div>
              {activeTab === 'all' && (
                <input
                  type="text"
                  placeholder="Search skills..."
                  value={skillsSearch}
                  onChange={(e) => setSkillsSearch(e.target.value)}
                  className={styles.skillsSearch}
                />
              )}
              <ul className={styles.skillsUl}>
                {displayedSkills
                  .filter(skill => skill.toLowerCase().includes(skillsSearch.toLowerCase()))
                  .map((skill, index) => (
                    <li key={index} className={styles.skillsLi}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleSkillClick(skill);
                        }}
                        className={styles.skillLink}
                      >
                        {skill}
                      </a>
                    </li>
                  ))}
                {displayedSkills.filter(skill => 
                  skill.toLowerCase().includes(skillsSearch.toLowerCase())
                ).length === 0 && (
                  <li className={styles.skillsLi}>No matching skills found</li>
                )}
              </ul>
            </div>
          </div>
        </form>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.tableHeader}>Date</th>
            <th className={`${styles.tableHeader} ${styles.authorColumn}`}>Author</th>
            <th className={styles.tableHeader}>Note</th>
            <th className={styles.tableHeader}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {notes.length > 0 ? (
            notes.map((note) => (
              <tr key={note.note_id}>
                <td className={styles.tableCell}>{new Date(note.created_at).toLocaleDateString()}</td>
                <td className={`${styles.tableCell} ${styles.authorColumn}`}>{note.author_username}</td>
                <td className={styles.tableCell}>
                  {note.note.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < note.note.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </td>
                <td className={styles.tableCell}>
                  <div className={styles.actionButtons}>
                    <button
                      className={styles.iconButton}
                      onClick={() => handleEdit(note.note_id)}
                      title="Edit"
                    >
                      <img src={PencilIcon} alt="Edit" className={styles.actionIcon} />
                    </button>
                    <button
                      className={styles.iconButton}
                      onClick={() => setNoteToDelete(note)}
                      title="Delete"
                    >
                      <img src={TrashIcon} alt="Delete" className={styles.actionIcon} />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={4} className={styles.tableCell}>No notes found.</td>
            </tr>
          )}
        </tbody>
      </table>

      {noteToDelete && (
        <>
          <div className={styles.dialogOverlay} onClick={() => setNoteToDelete(null)} />
          <div className={styles.dialog}>
            <p>
              Are you sure you want to delete this note?<br /><br />
              This action CANNOT BE UNDONE.
            </p>
            <div className={styles.dialogButtons}>
              <button onClick={() => handleDelete(noteToDelete.note_id)} className={styles.dialogButton}>Yes, Delete</button>
              <button onClick={() => setNoteToDelete(null)} className={styles.dialogCancelButton}>Cancel</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotesPage; 
